import { useState, useEffect, useCallback } from "react";
import { api, setTokens, clearTokens, getAccessToken } from "../lib/api";

interface User {
  userId: string;
  companyId: string;
  email: string;
  name?: string;
  role: "owner" | "technician";
}

interface AuthState {
  user: User | null;
  loading: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
  });

  const checkAuth = useCallback(async () => {
    if (!getAccessToken()) {
      setState({ user: null, loading: false });
      return;
    }

    try {
      const data = await api("/auth/me");
      setState({
        user: {
          userId: data.user.id,
          companyId: data.user.company_id,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role,
        },
        loading: false,
      });
    } catch {
      clearTokens();
      setState({ user: null, loading: false });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    const data = await api("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setTokens(data.accessToken, data.refreshToken);
    setState({
      user: data.user,
      loading: false,
    });
  };

  const register = async (
    companyName: string,
    name: string,
    email: string,
    password: string
  ) => {
    const data = await api("/auth/register", {
      method: "POST",
      body: JSON.stringify({ companyName, name, email, password }),
    });
    setTokens(data.accessToken, data.refreshToken);
    setState({
      user: data.user,
      loading: false,
    });
  };

  const logout = () => {
    clearTokens();
    setState({ user: null, loading: false });
  };

  return { ...state, login, register, logout };
}
